// testData.js
// Test harness for the booking ↔ shift linkage (and future linkages). Lets
// Dan spin up a synthetic "today's public event" booking with one tap — so
// the ShiftManager suggestion card, the CRM "View shift →" link, and the
// /library deep-link can be exercised end-to-end in seconds instead of the
// usual CRM-form-fill ritual.
//
// Every doc written by this utility carries `_isTestData: true`. Live views
// filter that flag out so test docs don't bleed into real CRM lists, the
// shift archive, or /events. A single `cleanTestData()` sweep purges them
// all across bookings / shifts / shift_summaries / orders.
//
// If Dan opens a shift off a test booking, the ShiftManager flow propagates
// `_isTestData: true` onto the shift doc (see openShift) so the shift is
// cleaned up in the same sweep.

import { db } from "../firebase";
import {
  collection, getDocs, query, where, runTransaction,
  addDoc, deleteDoc, setDoc, doc, Timestamp, serverTimestamp,
} from "firebase/firestore";

const TEST_FLAG = "_isTestData";

// ── SEED ──────────────────────────────────────────────────────────────────
// Creates a booking for today, flagged as public + confirmed, with enough
// shape to trigger the ShiftManager pre-fill suggestion. Returns the new
// booking's id so the caller can deep-link straight to it.
export async function seedTestBooking() {
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tenAm   = new Date(todayMidnight); tenAm.setHours(10, 0, 0, 0);
  const twoPm   = new Date(todayMidnight); twoPm.setHours(14, 0, 0, 0);

  const payload = {
    // Booking shape mirrors CRM defaults — only fields the linkage flow
    // actually touches are meaningful; the rest are reasonable stubs so
    // the booking renders cleanly in the CRM detail view if inspected.
    eventName:       "🧪 Test booking — safe to delete",
    eventType:       "Farmers Market",
    venueName:       "Test Venue",
    venueAddress:    "4257 Magnolia Ave, St. Louis, MO 63110",
    confirmedDate:   Timestamp.fromDate(todayMidnight),
    confirmedStart:  Timestamp.fromDate(tenAm),
    confirmedEnd:    Timestamp.fromDate(twoPm),
    isPublicEvent:   true,
    publicTitle:     "🧪 Test Booking",
    publicDescription: "Seeded by the /help → Dev Tools test harness. Safe to ignore.",
    status:          "Booked",
    name:            "Test Customer",
    email:           "test@thejoyofpancakes.com",
    phone:           "",
    totalRate:       0,
    submittedAt:     serverTimestamp(),
    createdAt:       serverTimestamp(),
    updatedAt:       serverTimestamp(),
    [TEST_FLAG]:     true,
  };

  const ref = await addDoc(collection(db, "bookings"), payload);
  return { id: ref.id };
}

// ── PREVIEW TOKEN ─────────────────────────────────────────────────────────
// Creates a fresh unused token for a given ticket type so the guest ordering
// experience can be previewed without printing or emailing. Requires an active
// shift (the order flow validates one exists). Returns the full /order?t= URL.
export async function createPreviewToken(ticketType = "custom") {
  const tokenId = crypto.randomUUID();
  await setDoc(doc(db, "tokens", tokenId), {
    id: tokenId,
    createdAt: serverTimestamp(),
    expiresAt: null,
    usedAt: null,
    status: "issued",
    ticketType,
    source: "test_harness",
    issuedBy: "dev_tools",
    [TEST_FLAG]: true,
  });
  const url = `${window.location.origin}/order?t=${tokenId}`;
  return { tokenId, url };
}

// ── SEED TEST ORDERS ──────────────────────────────────────────────────────
// Injects fake queued orders into the active shift for testing queue features.
// Each order gets a real token doc, a real queue number (via the shift counter),
// and realistic field shapes. Mix of order types: custom with notes, flash,
// custom without notes — so notes modal, name display, etc. can all be tested.
const TEST_GUESTS = [
  { recipientName: "Julia",   subjectName: "her cat Mochi",     notes: "She's a calico — orange, black, white patches. Extra fluffy!",  orderType: "custom",          favoriteColors: ["orange", "pink"] },
  { recipientName: "Marcus",  subjectName: null,                 notes: "",                                                               orderType: "flash",           designId: "saturnina", designLabel: "Saturnina", favoriteColors: ["blue", "indigo"] },
  { recipientName: "Theo",    subjectName: "a fire truck",       notes: "",                                                               orderType: "custom",          favoriteColors: ["red", "yellow"] },
  { recipientName: "Priya",   subjectName: "her daughter Meena", notes: "She just turned 5! Make it sparkly and magical if you can.",     orderType: "custom",          favoriteColors: ["violet", "pink", "yellow"] },
];

export async function seedTestOrders(activeShiftId, count = 4) {
  if (!activeShiftId) throw new Error("No active shift — open a shift first");
  const guests = TEST_GUESTS.slice(0, Math.min(count, TEST_GUESTS.length));
  const created = [];

  for (const guest of guests) {
    // Assign queue number atomically (same pattern as CustomerOrderView)
    const counterSnap = await getDocs(collection(db, "shifts", activeShiftId, "meta"));
    let queueNumber = null;
    if (!counterSnap.empty) {
      const counterRef = counterSnap.docs[0].ref;
      queueNumber = await runTransaction(db, async (tx) => {
        const counterDoc = await tx.get(counterRef);
        const current = counterDoc.data().lastNumber ?? 0;
        const next = current + 1;
        tx.update(counterRef, { lastNumber: next });
        return next;
      });
    }

    // Create token
    const tokenId = crypto.randomUUID();
    await setDoc(doc(db, "tokens", tokenId), {
      id: tokenId,
      createdAt: serverTimestamp(),
      expiresAt: null,
      usedAt: serverTimestamp(),
      status: "used",
      ticketType: guest.orderType === "flash" ? "flash" : "custom",
      source: "test_harness",
      issuedBy: "dev_tools",
      [TEST_FLAG]: true,
    });

    // Create order
    const orderRef = await addDoc(collection(db, "orders"), {
      tokenId,
      shiftId:              activeShiftId,
      orderType:            guest.orderType,
      state:                "queued",
      queueNumber,
      recipientName:        guest.recipientName,
      subjectName:          guest.subjectName || null,
      notes:                guest.notes || "",
      favoriteColors:       guest.favoriteColors || [],
      designId:             guest.designId || null,
      designLabel:          guest.designLabel || null,
      submittedAt:          serverTimestamp(),
      originalQueuePosition: queueNumber,
      suspensionCount:      0,
      portalEnabled:        true,
      portal_state:         "active",
      nft_status:           "none",
      // Nullable stubs
      photo_url: null, photo_cutout_url: null, customer_email: null,
      email_marketing_opt_in: false, ipfs_url: null,
      event_name: null, event_location: null, keepsake_expires_at: null,
      startedAt: null, flippedAt: null, completedAt: null,
      rejectedAt: null, suspendedAt: null, resubmittedAt: null,
      drawingTimeMs: null, cookTimeMs: null,
      imageUrl: null, croppedImageUrl: null,
      [TEST_FLAG]: true,
    });

    created.push({ id: orderRef.id, name: guest.recipientName, queueNumber });
  }

  return created;
}

// ── SEED TEST PRE-UPLOADS ─────────────────────────────────────────────────
// Stages a handful of fake pre-uploaded orders against a booking. Exercises
// the staging tray on /queue + the CRM BookingDetail "Pre-uploaded" panel
// without needing the client-facing upload form (step 3) to be live yet.
//
// Auto-discovers the target booking: prefers the active shift's bookingId
// (so the staging tray immediately shows them); falls back to any
// _isTestData booking on today's calendar. Throws if neither is available.
//
// state="staged", shiftId omitted, bookingId set. Designs vary so promotion
// can be tested across flash + freestyle + custom paths.
const TEST_PREUPLOADS = [
  { recipientName: "Edna",   subjectName: null,                       orderType: "flash",           designId: "saturnina", designLabel: "Saturnina", favoriteColors: ["pink", "violet"] },
  { recipientName: "Walter", subjectName: "his '57 Chevy",            orderType: "custom",          notes: "Cherry red, lots of chrome. He restored it himself.", favoriteColors: ["red", "yellow"] },
  { recipientName: "Mae",    subjectName: "a daffodil",               orderType: "freestyle_flash", isFreestyle: true, freestyleText: "a daffodil", favoriteColors: ["yellow", "green"] },
];

export async function seedTestPreUploads({ bookingId = null } = {}) {
  // Resolve target booking. Active shift first (most useful for testing),
  // then today's test booking, then bail.
  let resolvedBookingId = bookingId;
  if (!resolvedBookingId) {
    const activeShiftSnap = await getDocs(query(collection(db, "shifts"), where("isActive", "==", true)));
    if (!activeShiftSnap.empty) {
      const shift = activeShiftSnap.docs[0].data();
      if (shift.bookingId) resolvedBookingId = shift.bookingId;
    }
  }
  if (!resolvedBookingId) {
    const testBookingsSnap = await getDocs(query(collection(db, "bookings"), where(TEST_FLAG, "==", true)));
    if (!testBookingsSnap.empty) resolvedBookingId = testBookingsSnap.docs[0].id;
  }
  if (!resolvedBookingId) {
    throw new Error("No booking to attach to — open a shift linked to a booking, or seed a test booking first.");
  }

  // Create a real preupload token so the /upload?t= client form can be
  // exercised end-to-end from this seed. Cap is high enough that test
  // submits from the client form won't trip exhausted state.
  const tokenId = `preupload_test_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  // Token is created first with an empty orderIds[]; we patch each new
  // order's id in below as we create them. Mirrors the real submitPreUpload
  // flow so the public /upload client view (which reads orderIds straight
  // off the token doc) works against seeded data identically.
  const createdIds = [];
  await setDoc(doc(db, "tokens", tokenId), {
    id: tokenId,
    source: "preupload",
    status: "active",
    bookingId: resolvedBookingId,
    createdBy: "test_harness",
    maxUploads: 10,
    usedCount: TEST_PREUPLOADS.length,
    orderIds: [],
    expiresAt: null,
    createdAt: serverTimestamp(),
    lastTouchedAt: serverTimestamp(),
    [TEST_FLAG]: true,
  });

  const created = [];
  for (const g of TEST_PREUPLOADS) {
    const orderRef = await addDoc(collection(db, "orders"), {
      isPreUpload:       true,
      preUploadTokenId:  tokenId,
      bookingId:         resolvedBookingId,
      state:             "staged",
      stagedAt:          serverTimestamp(),
      holdIndefinitely:  false,
      orderType:         g.orderType,
      recipientName:     g.recipientName,
      subjectName:       g.subjectName || null,
      notes:             g.notes || "",
      favoriteColors:    g.favoriteColors || [],
      designId:          g.designId || null,
      designLabel:       g.designLabel || null,
      isFreestyle:       !!g.isFreestyle,
      freestyleText:     g.freestyleText || null,
      // Nullable stubs to keep the order shape consistent with normal flow
      shiftId:           null,
      queueNumber:       null,
      submittedAt:       null,
      photo_url:         null,
      photo_cutout_url:  null,
      imageUrl:          null,
      croppedImageUrl:   null,
      customer_email:    null,
      startedAt:         null, flippedAt: null, completedAt: null,
      drawingTimeMs:     null, cookTimeMs: null,
      createdAt:         serverTimestamp(),
      [TEST_FLAG]:       true,
    });
    created.push({ id: orderRef.id, name: g.recipientName });
    createdIds.push(orderRef.id);
  }

  // Patch the token with the final orderIds[] so the public /upload page
  // can fetch each by id (no list query needed).
  await setDoc(doc(db, "tokens", tokenId), { orderIds: createdIds }, { merge: true });

  return { bookingId: resolvedBookingId, tokenId, created };
}

// ── COUNT ─────────────────────────────────────────────────────────────────
// Cheap status readout for the dev-tools UI. Three parallel equality reads,
// no composite indexes required (single-field where on _isTestData is
// automatic in Firestore).
export async function countTestData() {
  const [bk, sh, ss, or, tk, rc, so] = await Promise.all([
    getDocs(query(collection(db, "bookings"),         where(TEST_FLAG, "==", true))).then(s => s.size).catch(() => 0),
    getDocs(query(collection(db, "shifts"),           where(TEST_FLAG, "==", true))).then(s => s.size).catch(() => 0),
    getDocs(query(collection(db, "shift_summaries"),  where(TEST_FLAG, "==", true))).then(s => s.size).catch(() => 0),
    getDocs(query(collection(db, "orders"),           where(TEST_FLAG, "==", true))).then(s => s.size).catch(() => 0),
    getDocs(query(collection(db, "tokens"),           where(TEST_FLAG, "==", true))).then(s => s.size).catch(() => 0),
    getDocs(query(collection(db, "publicShiftRecaps"), where(TEST_FLAG, "==", true))).then(s => s.size).catch(() => 0),
    getDocs(query(collection(db, "shopOrders"),       where(TEST_FLAG, "==", true))).then(s => s.size).catch(() => 0),
  ]);
  return { bookings: bk, shifts: sh, shiftSummaries: ss, orders: or, tokens: tk, recaps: rc, shopOrders: so };
}

// ── CLEANUP ───────────────────────────────────────────────────────────────
// Sweeps all test-flagged docs across the four collections. Returns a
// per-collection count of what was removed. Failures on individual docs are
// swallowed so one stuck doc doesn't block the rest of the sweep.
export async function cleanTestData() {
  // publicShiftRecaps: buildShiftRecap stamps _isTestData onto recaps of test
  // shifts (flag-and-filter) — purge those too so closed Lab shifts don't
  // leave public recap docs behind. Staff delete is allowed by rules.
  // shopOrders: DANTEST checkouts are stamped _isTestData by processShopPayment.
  // campaigns: Lab campaign-landing fixtures (createCampaignFixture).
  const collections = ["bookings", "shifts", "shift_summaries", "orders", "tokens", "publicShiftRecaps", "shopOrders", "campaigns", "baskets"];
  const removed = { bookings: 0, shifts: 0, shift_summaries: 0, orders: 0, tokens: 0, publicShiftRecaps: 0, shopOrders: 0, campaigns: 0, baskets: 0 };

  for (const coll of collections) {
    try {
      const snap = await getDocs(query(collection(db, coll), where(TEST_FLAG, "==", true)));
      for (const d of snap.docs) {
        try {
          await deleteDoc(doc(db, coll, d.id));
          removed[coll]++;
        } catch (err) {
          console.warn(`cleanTestData: failed to delete ${coll}/${d.id}`, err?.message || err);
        }
      }
    } catch (err) {
      console.warn(`cleanTestData: list failed on ${coll}`, err?.message || err);
    }
  }

  return removed;
}

// ── COUPON SEEDING ──────────────────────────────────────────────────────────
// Seeds default coupon codes into config/coupons. Merge-safe — won't stomp
// existing codes or usage counts.
export async function seedCoupons() {
  await setDoc(doc(db, "config", "coupons"), {
    codes: {
      DANTEST: {
        type: "percentage",
        amount: 100,
        active: true,
        freeShipping: true,
        maxUses: null,
        usedCount: 0,
        description: "Internal testing — 100% off + free shipping",
      },
      PANCAKE10: {
        type: "percentage",
        amount: 10,
        active: true,
        maxUses: 50,
        usedCount: 0,
        description: "10% off promo",
      },
    },
  }, { merge: true });
  return { seeded: ["DANTEST", "PANCAKE10"] };
}
